



import React, { useState, useEffect, useRef } from "react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import axios from "axios";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../context/auth';
import { useNavigate } from 'react-router-dom';
import "../cssStyle/PercentageCardDesign.css";

function PercentageCardDesign() {
  const [showToggler, setShowToggler] = useState(true);
  const [symbolsData, setSymbolsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  
  const intervalRef = useRef(null);
  
  const { getAdminAuthToken } = useAuth();
  const navigate = useNavigate();
  const token = getAdminAuthToken();

  const formatPercentage = (value) => {
    if (!value && value !== 0) return '0';
    if (parseFloat(value) === 0) return null;
    return parseFloat(value).toFixed(1);
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return 'N/A';
    return `₹${parseFloat(price).toFixed(2)}`;
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    const date = new Date(time);
    return date.toLocaleString();
  };

  // ✅ Format average time difference - ONLY MINUTES (no seconds)
  const formatAvgTime = (seconds) => {
    if (!seconds && seconds !== 0) return null;
    if (seconds < 60) {
      return `< 1 min`;
    } else if (seconds < 3600) {
      const minutes = Math.round(seconds / 60);
      return `${minutes} min`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.round((seconds % 3600) / 60);
      if (minutes === 0) {
        return `${hours} hr`;
      }
      return `${hours} hr ${minutes} min`;
    }
  };

  // ✅ Calculate average time difference between signals
  const calculateAvgTimeDifference = (entries) => {
    if (!entries || entries.length < 2) return null;
    
    const sortedEntries = [...entries].sort((a, b) => new Date(a.time) - new Date(b.time));
    
    let totalDifference = 0;
    let differenceCount = 0;
    
    for (let i = 1; i < sortedEntries.length; i++) {
      const prevTime = new Date(sortedEntries[i - 1].time);
      const currTime = new Date(sortedEntries[i].time);
      const diffInSeconds = (currTime - prevTime) / 1000;
      totalDifference += diffInSeconds;
      differenceCount++;
    }
    
    return totalDifference / differenceCount;
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`, {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: false,
        draggable: false,
      });
    } catch (err) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.info(`${label} copied!`, {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: true,
      });
    }
  };

  const fetchData = async () => {
    if (!token) {
      setLoading(false);
      toast.error("Please login first", {
        position: "top-right",
        autoClose: 2000,
      });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }

    try {
      const response = await axios.get(
        "https://api.algocontroller.com/admin_app/getting_admin_analysis",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      const rawData = response.data?.data || [];
      
      const filteredData = [];
      rawData.forEach(insItem => {
        if (insItem.data && Array.isArray(insItem.data)) {
          const filteredTypesData = insItem.data.filter(item => 
            item.type === 'second' || item.type === 'third'
          );
          if (filteredTypesData.length > 0) {
            filteredData.push({
              ...insItem,
              data: filteredTypesData
            });
          }
        }
      });
      
      const symbolMap = new Map();
      
      filteredData.forEach(insItem => {
        if (insItem.data && Array.isArray(insItem.data)) {
          insItem.data.forEach(item => {
            const percentageValue = parseFloat(item.percentage);
            if (percentageValue === 0) return;
            
            const symbol = item.symbol;
            
            if (!symbolMap.has(symbol)) {
              symbolMap.set(symbol, {
                symbol: symbol,
                entries: []
              });
            }
            
            const symbolData = symbolMap.get(symbol);
            
            symbolData.entries.push({
              id: `${symbol}_${item.cat}_${item.time}_${Date.now()}_${Math.random()}`,
              cat: item.cat,
              percentage: percentageValue,
              fullPercentage: item.percentage,
              price: item.price,
              prev_row_close: item.prev_row_close,
              time: item.time,
              ins_id: item.ins_id,
              type: item.type
            });
          });
        }
      });
      
      const processedData = [];
      for (const [symbol, data] of symbolMap.entries()) {
        if (data.entries && data.entries.length > 0) {
          const sortedEntries = [...data.entries].sort((a, b) => new Date(a.time) - new Date(b.time));
          const avgTimeSeconds = calculateAvgTimeDifference(sortedEntries);
          
          processedData.push({
            symbol: symbol,
            entries: sortedEntries,
            entryCount: data.entries.length,
            avgTimeSeconds: avgTimeSeconds,
            avgTimeFormatted: formatAvgTime(avgTimeSeconds)
          });
        }
      }
      
      setSymbolsData(processedData);
      setLoading(false);
    } catch (err) {
      console.error("API Error:", err);
      if (err.response?.status === 401) {
        toast.error("Session expired. Please login again.", {
          position: "top-right",
          autoClose: 2000,
        });
        setTimeout(() => navigate('/login'), 2000);
      } else {
        toast.error("Failed to fetch data", {
          position: "top-right",
          autoClose: 2000,
        });
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      toast.error("Please login first", {
        position: "top-right",
        autoClose: 2000,
      });
      setTimeout(() => navigate('/login'), 2000);
      return;
    }
    
    fetchData();
    
    intervalRef.current = setInterval(() => {
      fetchData();
    }, 10000);
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [token]);

  const getBoxClass = (cat) => {
    const isDR = cat.includes('DR');
    const isBig = cat.includes('BIG');
    
    if (isBig) {
      return isDR ? 'big-box dr-big-box' : 'big-box ur-big-box';
    } else {
      return isDR ? 'small-box dr-small-box' : 'small-box ur-small-box';
    }
  };

  const handleMouseEnter = (e, entry, symbol, allEntries, avgTimeFormatted, avgTimeSeconds, entryCount) => {
    if (!entry) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    let leftPos = rect.right + 10;
    let topPos = rect.top;
    
    const tooltipWidth = 520;
    const tooltipHeight = 480;
    
    if (leftPos + tooltipWidth > viewportWidth) {
      leftPos = rect.left - tooltipWidth - 10;
    }
    if (leftPos < 10) leftPos = 10;
    if (topPos + tooltipHeight > viewportHeight) {
      topPos = viewportHeight - tooltipHeight - 10;
    }
    if (topPos < 10) topPos = 10;
    
    setHoverPosition({ x: leftPos, y: topPos });
    setHoveredItem({
      symbol,
      currentEntry: entry,
      allEntries: allEntries,
      avgTimeFormatted: avgTimeFormatted,
      avgTimeSeconds: avgTimeSeconds,
      entryCount: entryCount
    });
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  if (loading) {
    return (
      <div className="percentageCardDesign">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading market data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="percentageCardDesign">
      <ToastContainer 
        position="top-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover={false}
        limit={3}
      />
      <div className="toggler-container">
        <div
          className="toggler-header"
          onClick={() => setShowToggler(!showToggler)}
        >
          <h3>Market Categories ({symbolsData.length} Items)</h3>
          <span className="toggler-icon">
            {showToggler ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </span>
        </div>

        {showToggler && (
          <div className="toggler-content">
            <div className="toggler-container-main-card">
              
              {symbolsData.map((card, index) => {
                if (!card || !card.entries || card.entries.length === 0) return null;
                
                return (
                  <div key={index} className="toggler-content-inner">
                    {/* Symbol with Count Badge and Avg Time */}
                    <div 
                      className="toggler-content-item"
                      onClick={() => copyToClipboard(card.symbol, card.symbol)}
                      style={{ cursor: 'pointer' }}
                      title="Click to copy symbol"
                    >
                      <span className="symbol-name">{card.symbol}</span>
                      <div className="symbol-stats">
                        <span className="symbol-count-badge"> {card.entryCount}</span>
                        {card.avgTimeFormatted && (
                          <span className="avg-time-badge">{card.avgTimeFormatted}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="entries-container">
                      {card.entries.map((entry, idx) => {
                        const percentage = formatPercentage(entry.percentage);
                        if (!percentage) return null;
                        
                        return (
                          <div 
                            key={entry.id || idx}
                            className={getBoxClass(entry.cat)}
                            onMouseEnter={(e) => handleMouseEnter(
                              e, entry, card.symbol, card.entries, 
                              card.avgTimeFormatted, card.avgTimeSeconds, card.entryCount
                            )}
                            onMouseLeave={handleMouseLeave}
                          >
                            {percentage}%
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              
            </div>
          </div>
        )}
      </div>

      {/* Tooltip with Full Details */}
      {hoveredItem && hoveredItem.allEntries && (
        <div
          className="signal-tooltip"
          style={{
            position: 'fixed',
            left: `${hoverPosition.x}px`,
            top: `${hoverPosition.y}px`,
            zIndex: 9999,
          }}
        >
          <div className="signal-tooltip-content">
            <div className="tooltip-header">
              <span className="tooltip-symbol">📌 {hoveredItem.symbol}</span>
              <span className={`tooltip-type ${hoveredItem.currentEntry.cat.includes('DR') ? 'type-dr' : 'type-ur'}`}>
                {hoveredItem.currentEntry.cat}
              </span>
            </div>

            <div className="tooltip-stats">
              <div className="tooltip-row">
                <span className="tooltip-label">📊 Total Signals:</span>
                <span className="tooltip-value">{hoveredItem.entryCount}</span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">⏱️ Avg Time Difference:</span>
                <span className="tooltip-value avg-time-value">
                  {hoveredItem.avgTimeFormatted || 'N/A (need ≥2 signals)'}
                </span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">🎯 Current Signal:</span>
                <span className="tooltip-value">
                  {hoveredItem.currentEntry.fullPercentage || hoveredItem.currentEntry.percentage}%
                </span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">💰 Price:</span>
                <span className="tooltip-value">{formatPrice(hoveredItem.currentEntry.price)}</span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">📉 Previous Close:</span>
                <span className="tooltip-value">{formatPrice(hoveredItem.currentEntry.prev_row_close)}</span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">🕐 Current Time:</span>
                <span className="tooltip-value">{formatTime(hoveredItem.currentEntry.time)}</span>
              </div>
              <div className="tooltip-row">
                <span className="tooltip-label">🆔 ID:</span>
                <span className="tooltip-value">{hoveredItem.currentEntry.ins_id}</span>
              </div>
            </div>

            <div className="signal-table-wrapper">
              <div className="signal-table">
                <div className="signal-table-header">
                  <span>📅 Datetime</span>
                  <span>📊 Percentage</span>
                  <span>🔖 Symbol Name</span>
                </div>
                <div className="signal-table-body">
                  {hoveredItem.allEntries.map((entry, idx) => (
                    <div key={idx} className="signal-table-row">
                      <span className="datetime-col">{formatTime(entry.time)}</span>
                      <span className={`percentage-col ${entry.cat.includes('DR') ? 'dr-text' : 'ur-text'}`}>
                        {entry.fullPercentage || entry.percentage}%
                      </span>
                      <span className="symbol-col">{hoveredItem.symbol}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="tooltip-footer">
              ✨ Click on symbol name to copy | Total {hoveredItem.entryCount} signals
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PercentageCardDesign;

